import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { AnchoredHoverPopover } from "@/components/AnchoredHoverPopover";
import { BidCell } from "@/components/BidCell";
import {
  ALL_COLUMNS,
  MIN_COLUMN_WIDTH,
  type ColumnKey,
} from "@/lib/columns-config";
import { StatusDot } from "@/components/StatusDot";
import { TranslationValue } from "@/components/TranslationCell";
import { CpaCell, IpmBadge, RoasCell } from "@/components/MetricCells";
import { LabelChip } from "@/components/LabelChip";
import { useLabelsContext } from "@/lib/labels-context";
import { fmtInt, fmtMoney, fmtMoneyTrim } from "@/lib/fmt";
import { ruleChipLabel } from "@/lib/rule-chip-label";
import {
  deviceSubRows,
  deviceTotalRows,
  deviceTotalsPlacement,
  splitsByDevice,
} from "@/lib/device-split";
import {
  computeTotals,
  formatTotal,
  totalValue,
  totalsFromServer,
  type Totals,
} from "@/lib/footer-totals";
import type {
  AdGroupStatsRow,
  CampaignStatsRow,
  EntityTotalsRow,
  KeywordStatsRow,
  AppliedRule,
} from "@/lib/types";

export type Grain = "campaign" | "ad_group" | "keyword";

/** Minimum row count before Total / Sub Total tfoot rows appear. With
 *  a sparse result set (2-3 rows) the totals would otherwise park
 *  immediately under the data and read as "another data row"; padding
 *  to at least this many rows keeps the totals visually anchored to
 *  where the eye expects them after a real list. */
const MIN_ROWS_BEFORE_TOTALS = 10;

type RowFor<G extends Grain> = G extends "campaign"
  ? CampaignStatsRow
  : G extends "ad_group"
    ? AdGroupStatsRow
    : KeywordStatsRow;

export type RowSelection = {
  /** Ids of rows currently selected. */
  ids: Set<number>;
  onToggle: (id: number) => void;
  /** When provided, enables the header checkbox that toggles every row
   *  currently in view. */
  onToggleAll?: (visibleIds: number[], selectAll: boolean) => void;
};

type StatsTableProps<G extends Grain> = {
  grain: G;
  rows: RowFor<G>[];
  visibleCols: ColumnKey[];
  /**
   * Fires when the row's *label* is clicked (for drill-down nav). The
   * row-level click handler is deliberately not the whole tr — users
   * want to click metric cells without triggering navigation.
   */
  onRowClick?: (row: RowFor<G>) => void;
  /** Enables the checkbox column on the left. */
  selection?: RowSelection;
  /** Inline-edit support — when `true` the corresponding money cell
   *  switches into a click-to-edit input (no separate Edit button).
   *  Each grain only edits its own canonical money field:
   *    - campaign  → daily_budget
   *    - ad_group  → default_bid
   *    - keyword   → bid
   *  All three commit through the existing PATCH endpoints + ASA push
   *  pipeline (pending_writes drainer). */
  canEditMoney?: boolean;
  /** Строки, которые править нельзя (кампании групп Apple с доступом только
   *  на чтение): денежные ячейки у них не открываются на правку. */
  readOnlyRowIds?: Set<number>;
  /** Rendered inside the table when there are no rows (the header stays);
   *  default is the plain "No … match" line. */
  emptyContent?: ReactNode;
  onCommitDailyBudget?: (campaign_id: number, daily_budget: number) => void;
  onCommitTargetCpa?: (campaign_id: number, target_cpa: number) => void;
  onCommitDefaultBid?: (ad_group_id: number, default_bid: number) => void;
  onCommitCpaGoal?: (ad_group_id: number, cpa_goal: number) => void;
  onCommitBid?: (keyword_id: number, bid: number) => void;
  /** When provided, the bottom Totals row sums these server-computed
   *  numbers instead of summing the visible (paginated) rows. Use
   *  this whenever the entity list has a `total > rows.length` —
   *  otherwise the footer would show only the current page's sums
   *  and disagree with Overview / global aggregates. */
  serverTotals?: EntityTotalsRow | null;
  /** Always-on bottom "Total by …" row data — sums across the full
   *  scope (org+app+date), ignoring status / search / etc. Falls
   *  back to `serverTotals` when not supplied (legacy callers).
   *  Pair with `totalsLabel` for the row label. */
  unfilteredTotals?: EntityTotalsRow | null;
  /** Label for the always-on bottom row. Examples:
   *    - "Total by Universal Remote"  (Campaigns page, single app)
   *    - "Total by CTMOBI"            (Campaigns page, all apps)
   *    - "Total"                      (CampaignDetailPage tabs)
   *  Defaults to "Total" when omitted. */
  totalsLabel?: string;
  /** Count badge next to the totals label (e.g. "519 campaigns").
   *  Pulled from CampaignsResponse.unfiltered_count for the totals-by
   *  row, or from rows.length on detail pages. */
  totalsCount?: number;
  /** Word for the count unit — "campaigns" / "ad groups" / "keywords"
   *  / "search terms". Defaults to "rows" so the legacy footer label
   *  ("30 rows") still works for callers that don't pass this. */
  totalsCountWord?: string;
  /** Optional second footer row that appears ABOVE the always-on
   *  "Total by …" row when in-table filters narrow the visible set.
   *  Show "Total filtered campaigns: N" / "Selected ad groups: N"
   *  with `serverTotals` summing only the visible rows. */
  filteredTotalsLabel?: string;
  /** Count for the filtered row (visible rows after status / search). */
  filteredTotalsCount?: number;
  /** Server-driven sort. `sort.by = null` means default backend
   *  ordering (spend desc). When set, the column header gets a
   *  ▲ / ▼ glyph and clicking it cycles through the column's
   *  asc/desc/off states via onSortChange. */
  sort?: { by: string | null; dir: "asc" | "desc" };
  onSortChange?: (next: { by: string | null; dir: "asc" | "desc" }) => void;
  /** Previous rows shown while a new sort / page / search is loading. */
  busy?: boolean;
  /** «Split by devices»: two sub-rows (iPhone, iPad) under every row from
   *  `row.device_split`. Sub-rows follow their row — sorting stays by rows. */
  deviceSplit?: boolean;
  /** «Split by devices» footer: `device_totals` from the listing — the
   *  Total filtered row broken down by device over every filtered entity.
   *  With `deviceSplit` on, two lines (Total for iPhone, Total for iPad)
   *  go under the Total filtered row; Total by <App> is never split. */
  deviceTotals?: Partial<Record<string, EntityTotalsRow | null>> | null;
  /** Per-column pixel widths. Empty / missing key → default width.
   *  The identity column uses the literal key `_identity_column`.
   *  When provided alongside `onWidthChange`, the right edge of each
   *  column header becomes a drag-handle. */
  widths?: Record<string, number>;
  /** Commits a column resize. Fires on mouseup, NOT on each pixel of
   *  drag — caller persists to localStorage / preset. */
  onWidthChange?: (key: string, px: number) => void;
};

/** Storage key used inside `widths` for the always-leftmost identity
 *  column (campaign name / ad group name / keyword text). Not a real
 *  ColumnKey — just a sentinel so the resize state can address it. */
export const IDENTITY_WIDTH_KEY = "_identity_column";

// Columns the backend supports server-side sorting for. Frontend
// shows a click-cursor + arrow only on these; clicks on others are
// no-ops. Source-of-truth on the backend:
// app.services.stats_service._build_outer_order_by — keep in sync.
const SORTABLE_KEYS: ReadonlySet<ColumnKey> = new Set<ColumnKey>([
  // base counts + revenue
  "spend", "impressions", "taps", "installs",
  "installs_tap", "installs_view", "installs_total",
  "new_downloads_tap", "new_downloads_view", "new_downloads_total",
  "redownloads_tap", "redownloads_view", "redownloads_total",
  "trials", "subscriptions", "revenue",
  // derived ratios — `ttr`/`avg_cpt`/`cr_tap`/`cr_total` are
  // frontend-rendered ratios; backend got matching ORDER BY exprs
  // in stats_service so the click round-trips.
  "ipm", "cpm", "ttr", "avg_cpt",
  "cpa", "cpa_tap", "cpa_view", "cpa_total",
  "cr_tap", "cr_total",
  "trial_cvr", "paid_cvr",
  // cohort revenue + ROAS / ROI / ARPU at every supported window
  "revenue_d1", "revenue_d4", "revenue_d7", "revenue_d10",
  "revenue_d20", "revenue_d30", "revenue_d35",
  "roas_d1", "roas_d4", "roas_d7", "roas_d10",
  "roas_d20", "roas_d30", "roas_d35",
  "roi_d1", "roi_d4", "roi_d7", "roi_d10",
  "roi_d20", "roi_d30", "roi_d35",
  "arpu_d1", "arpu_d4", "arpu_d7", "arpu_d10",
  "arpu_d20", "arpu_d30", "arpu_d35",
  // meta — campaign
  "daily_budget", "target_cpa", "status", "last_modified_at", "modified_by",
  "creation_time",
  "rule_applied", "rule_names",
  // meta — ad_group
  "default_bid", "cpc_bid", "cpa_goal",
  // meta — keyword
  "bid", "match_type",
  // per-event count + revenue + cr + cpa (18 events × 4)
  "trial_started", "trial_started_revenue", "trial_started_cr", "trial_started_cpa",
  "trial_converted", "trial_converted_revenue", "trial_converted_cr", "trial_converted_cpa",
  "trial_renewal_cancelled", "trial_renewal_cancelled_revenue", "trial_renewal_cancelled_cr", "trial_renewal_cancelled_cpa",
  "trial_renewal_reactivated", "trial_renewal_reactivated_revenue", "trial_renewal_reactivated_cr", "trial_renewal_reactivated_cpa",
  "trial_expired", "trial_expired_revenue", "trial_expired_cr", "trial_expired_cpa",
  "subscription_started", "subscription_started_revenue", "subscription_started_cr", "subscription_started_cpa",
  "subscription_renewed", "subscription_renewed_revenue", "subscription_renewed_cr", "subscription_renewed_cpa",
  "subscription_renewal_cancelled", "subscription_renewal_cancelled_revenue", "subscription_renewal_cancelled_cr", "subscription_renewal_cancelled_cpa",
  "subscription_renewal_reactivated", "subscription_renewal_reactivated_revenue", "subscription_renewal_reactivated_cr", "subscription_renewal_reactivated_cpa",
  "subscription_expired", "subscription_expired_revenue", "subscription_expired_cr", "subscription_expired_cpa",
  "subscription_paused", "subscription_paused_revenue", "subscription_paused_cr", "subscription_paused_cpa",
  "subscription_deferred", "subscription_deferred_revenue", "subscription_deferred_cr", "subscription_deferred_cpa",
  "subscription_refunded", "subscription_refunded_revenue", "subscription_refunded_cr", "subscription_refunded_cpa",
  "non_subscription_purchase", "non_subscription_purchase_revenue", "non_subscription_purchase_cr", "non_subscription_purchase_cpa",
  "non_subscription_purchase_refunded", "non_subscription_purchase_refunded_revenue", "non_subscription_purchase_refunded_cr", "non_subscription_purchase_refunded_cpa",
  "entered_grace_period", "entered_grace_period_revenue", "entered_grace_period_cr", "entered_grace_period_cpa",
  "billing_issue_detected", "billing_issue_detected_revenue", "billing_issue_detected_cr", "billing_issue_detected_cpa",
  "access_level_updated", "access_level_updated_revenue", "access_level_updated_cr", "access_level_updated_cpa",
  // Adjust per-event count + revenue + cr + cpa (10 events; only the 5
  // purchase-like ones carry revenue). Backend resolves these through an
  // `adj_sort` CTE — stats_service._adjust_sort_key / _adjust_sort_expr.
  "adj_mobilepurchase", "adj_mobilepurchase_revenue", "adj_mobilepurchase_cr", "adj_mobilepurchase_cpa",
  "adj_rv_nonsubscriptionpurchase", "adj_rv_nonsubscriptionpurchase_revenue", "adj_rv_nonsubscriptionpurchase_cr", "adj_rv_nonsubscriptionpurchase_cpa",
  "adj_rv_productchange", "adj_rv_productchange_cr", "adj_rv_productchange_cpa",
  "adj_rv_subscriptioncancel", "adj_rv_subscriptioncancel_cr", "adj_rv_subscriptioncancel_cpa",
  "adj_rv_subscriptionexpiration", "adj_rv_subscriptionexpiration_cr", "adj_rv_subscriptionexpiration_cpa",
  "adj_rv_subscriptioninitialpurchase", "adj_rv_subscriptioninitialpurchase_revenue", "adj_rv_subscriptioninitialpurchase_cr", "adj_rv_subscriptioninitialpurchase_cpa",
  "adj_rv_subscriptionrenewal", "adj_rv_subscriptionrenewal_revenue", "adj_rv_subscriptionrenewal_cr", "adj_rv_subscriptionrenewal_cpa",
  "adj_rv_subscriptiontrialactivate", "adj_rv_subscriptiontrialactivate_revenue", "adj_rv_subscriptiontrialactivate_cr", "adj_rv_subscriptiontrialactivate_cpa",
  "adj_rv_subscriptiontrialcancel", "adj_rv_subscriptiontrialcancel_cr", "adj_rv_subscriptiontrialcancel_cpa",
  "adj_rv_subscriptiontrialstart", "adj_rv_subscriptiontrialstart_cr", "adj_rv_subscriptiontrialstart_cpa",
] as ColumnKey[]);

// Backend column-key alias: a few frontend ColumnKey names don't
// match the SQL alias 1:1. We translate on click so the user sees
// a familiar UI key while the backend gets the alias it knows.
const SORT_KEY_TO_BACKEND: Partial<Record<ColumnKey, string>> = {
  // base CTE aliases for spend / revenue retain the `_usd` suffix
  // even though the column-config keys are bare (`spend`, `revenue`).
  spend:          "spend_usd",
  revenue:        "revenue_usd",
  installs_tap:   "tap_installs",
  installs_view:  "view_installs",
  installs_total: "total_installs",
  // Both the "Rule Names" list and the "Rule Applied" dot sort on the same
  // backend field (applied_rule_names, a text[]) so rows with rules group
  // together and the "—" rows collect at one end — instead of interleaving.
  rule_names:     "applied_rule_names",
  rule_applied:   "applied_rule_names",
};

/** Clickable column header with ▲/▼ glyph for the active sort column.
 *  Cycle: not-sorted → desc → asc → not-sorted (back to backend default).
 *  Only attaches a click handler for keys in SORTABLE_KEYS so non-
 *  sortable columns (e.g. labels) don't get a misleading cursor.  */
function IdentityHeader({
  label, width, onResize,
}: {
  label: string;
  width?: number;
  onResize?: (px: number) => void;
}) {
  const thRef = useRef<HTMLTableCellElement | null>(null);
  const onHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!onResize || !thRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = thRef.current.getBoundingClientRect().width;
      const node = thRef.current;
      let liveW = startW;
      const onMove = (ev: MouseEvent) => {
        liveW = Math.max(MIN_COLUMN_WIDTH, startW + (ev.clientX - startX));
        if (node) node.style.width = `${liveW}px`;
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
        onResize(Math.round(liveW));
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      document.body.style.cursor = "col-resize";
    },
    [onResize],
  );
  // Identity column has a 280px default minWidth; explicit width takes
  // precedence when the user has resized it. Don't set `position:
  // relative` inline — that overrides the CSS `position: sticky` rule
  // on .dt thead th, breaking the sticky-thead behavior. `position:
  // sticky` already creates a containing block for the absolutely-
  // positioned .col-resize-handle child (same as `relative` would).
  const style: React.CSSProperties =
    width !== undefined
      ? { width, minWidth: width }
      : { minWidth: 280 };
  return (
    <th
      ref={thRef}
      className="sticky-col sticky-col-identity"
      style={style}
    >
      {label}
      {onResize && (
        <span
          className="col-resize-handle"
          onMouseDown={onHandleMouseDown}
          onClick={(e) => e.stopPropagation()}
          aria-hidden
          title="Drag to resize column"
        />
      )}
    </th>
  );
}

function SortableHeader({
  colKey, label, numeric, sort, onSortChange,
  width, onResize,
  extraClass, extraStyle,
}: {
  colKey: ColumnKey;
  label: string;
  numeric: boolean;
  sort?: { by: string | null; dir: "asc" | "desc" };
  onSortChange?: (next: { by: string | null; dir: "asc" | "desc" }) => void;
  width?: number;
  onResize?: (px: number) => void;
  extraClass?: string;
  extraStyle?: React.CSSProperties;
}) {
  const sortable = SORTABLE_KEYS.has(colKey) && !!onSortChange;
  const backendKey = SORT_KEY_TO_BACKEND[colKey] ?? colKey;
  const active = sortable && sort?.by === backendKey;
  const dir = active ? sort!.dir : null;
  const thRef = useRef<HTMLTableCellElement | null>(null);

  const onClick = () => {
    if (!sortable || !onSortChange) return;
    if (!active) {
      onSortChange({ by: backendKey, dir: "desc" });
    } else if (dir === "desc") {
      onSortChange({ by: backendKey, dir: "asc" });
    } else {
      onSortChange({ by: null, dir: "desc" });
    }
  };

  const onHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!onResize || !thRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = thRef.current.getBoundingClientRect().width;
      const node = thRef.current;
      let liveW = startW;
      const onMove = (ev: MouseEvent) => {
        liveW = Math.max(MIN_COLUMN_WIDTH, startW + (ev.clientX - startX));
        if (node) node.style.width = `${liveW}px`;
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
        onResize(Math.round(liveW));
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      document.body.style.cursor = "col-resize";
    },
    [onResize],
  );

  const baseStyle: React.CSSProperties = {
    ...extraStyle,
    ...(sortable ? { cursor: "pointer", userSelect: "none" } : null),
    ...(width !== undefined ? { width, minWidth: width } : null),
    // Do NOT set `position: relative` here — it overrides the CSS
    // `position: sticky` on .dt thead th, so the column header
    // doesn't pin when the user scrolls the table (only the
    // sticky-col cells, which set sticky in CSS without inline
    // override, end up working). `position: sticky` from the CSS
    // rule still creates a containing block for the absolute
    // `.col-resize-handle` child, just like `relative` would.
  };

  return (
    <th
      ref={thRef}
      className={[
        numeric ? "num" : "",
        sortable ? "sortable" : "",
        active ? "is-sorted" : "",
        extraClass ?? "",
      ].filter(Boolean).join(" ")}
      onClick={onClick}
      style={baseStyle}
    >
      <span className="th-label">{label}</span>
      {active && (
        <span className="sort-indicator" aria-hidden>
          {dir === "desc" ? "▼" : "▲"}
        </span>
      )}
      {onResize && (
        <span
          className="col-resize-handle"
          onMouseDown={onHandleMouseDown}
          onClick={(e) => e.stopPropagation()}
          aria-hidden
          title="Drag to resize column"
        />
      )}
    </th>
  );
}

/**
 * Single-level stats / config table. Renders identity column, optional
 * checkbox column, and the visible columns from ColumnsModal. Status
 * is shown via StatusDot (ASA-style "• Running") rather than the old
 * pill-shaped StatusBadge.
 *
 * Fields not present on the raw stats response (cpa_goal,
 * default_bid, daily_budget) are read from optional mirror fields on
 * the row — callers are expected to merge mirror data into stats rows
 * before passing them in.
 */
export function StatsTable<G extends Grain>({
  grain,
  rows,
  visibleCols,
  onRowClick,
  selection,
  emptyContent,
  canEditMoney = false,
  readOnlyRowIds,
  onCommitDailyBudget,
  onCommitTargetCpa,
  onCommitDefaultBid,
  onCommitCpaGoal,
  onCommitBid,
  serverTotals,
  unfilteredTotals,
  totalsLabel,
  totalsCount,
  totalsCountWord,
  filteredTotalsLabel,
  filteredTotalsCount,
  sort,
  onSortChange,
  busy = false,
  deviceSplit = false,
  deviceTotals,
  widths,
  onWidthChange,
}: StatsTableProps<G>) {
  const identityHeader =
    grain === "campaign"
      ? "Campaign name"
      : grain === "ad_group"
        ? "Ad group name"
        : "Keyword";

  // Honour the user's saved order (post-DnD) rather than the static
  // ALL_COLUMNS catalogue order. Drop unknown keys defensively.
  const cols = visibleCols
    .map((k) => ALL_COLUMNS.find((c) => c.key === k))
    .filter((c): c is (typeof ALL_COLUMNS)[number] => c !== undefined);

  const visibleIds = rows.map((r) => idOf(r, grain));
  const allSelected =
    selection !== undefined &&
    visibleIds.length > 0 &&
    visibleIds.every((id) => selection.ids.has(id));
  const someSelected =
    selection !== undefined &&
    visibleIds.some((id) => selection.ids.has(id)) &&
    !allSelected;

  // Prefer server-computed totals (sums across the FULL filtered set,
  // no pagination) when available — that's what makes the bottom row
  // agree with Overview. Fall back to client-side per-row sum for
  // legacy callers that don't pass `serverTotals` yet (or when the
  // entity list is empty).
  // `totals` drives the (optionally-rendered) "Total filtered ..."
  // row — the visible-set sum. When the caller doesn't pass
  // serverTotals we fall back to summing the page's rows, same as
  // before.
  const totals = serverTotals
    ? totalsFromServer(serverTotals)
    : computeTotals(rows);
  // `bottomTotals` drives the always-on "Total by …" footer row.
  // Falls back to `totals` so legacy callers (no unfiltered prop)
  // keep showing one row with the same numbers as today.
  const bottomTotals = unfilteredTotals
    ? totalsFromServer(unfilteredTotals)
    : totals;
  const showFilteredRow = !!filteredTotalsLabel && !!serverTotals;
  // The device lines belong to Total filtered. Where the table has no
  // separate unfiltered row (detail tabs pass the same totals twice), its
  // only footer row IS the filtered total and takes them.
  const devicePlacement = deviceTotalsPlacement({
    splitOn: deviceSplit,
    hasDeviceTotals: !!deviceTotals,
    filteredRowShown: showFilteredRow,
    singleFooter: !unfilteredTotals || unfilteredTotals === serverTotals,
  });
  const deviceLines = devicePlacement ? deviceTotalRows(deviceTotals) : [];
  // Sticky offsets (in row heights from the bottom) when the device lines
  // are in the footer; without them the footer keeps its old CSS as is.
  const footOff = (n: number) =>
    devicePlacement ? ({ "--foot-off": n } as CSSProperties) : undefined;
  const footStack = devicePlacement ? " foot-stack" : "";
  const bottomLabel = totalsLabel ?? "Total";
  const bottomCount = totalsCount ?? rows.length;
  const countUnit = totalsCountWord ?? "row";
  const filteredCount = filteredTotalsCount ?? rows.length;

  // Shift-click range select: remember the last row the user toggled
  // (anchor); a subsequent Shift+click selects every row between the two.
  const anchorRef = useRef<number | null>(null);
  const handleRowToggle = (id: number, shiftKey: boolean) => {
    if (!selection) return;
    const anchor = anchorRef.current;
    if (shiftKey && anchor != null && anchor !== id && selection.onToggleAll) {
      const a = visibleIds.indexOf(anchor);
      const b = visibleIds.indexOf(id);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        // Add the whole range in ONE update. onToggleAll adds a list at
        // once; a loop of onToggle wouldn't work — the parent's per-row
        // toggle reads its `selected` closure, so only the last id sticks.
        selection.onToggleAll(visibleIds.slice(lo, hi + 1), true);
        anchorRef.current = id;
        return;
      }
    }
    selection.onToggle(id);
    anchorRef.current = id;
  };

  // `has-selection` toggles which column is the second sticky-pinned
  // one (checkbox vs identity) — driven by CSS in app.css.
  const tableClass = `dt dt-sticky${selection ? " has-selection" : ""}${deviceSplit ? " dt-split" : ""}`;

  return (
    <div className={`table-scroll sticky-table${busy ? " is-busy" : ""}`} aria-busy={busy || undefined}>
      <table className={tableClass}>
        <thead>
          <tr>
            {selection && (
              <th
                className="sticky-col sticky-col-checkbox"
                style={{ width: 36, paddingLeft: 14 }}
              >
                {selection.onToggleAll && (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={() =>
                      selection.onToggleAll?.(visibleIds, !allSelected)
                    }
                    aria-label="Select all rows"
                  />
                )}
              </th>
            )}
            <IdentityHeader
              label={identityHeader}
              width={widths?.[IDENTITY_WIDTH_KEY]}
              onResize={
                onWidthChange
                  ? (px) => onWidthChange(IDENTITY_WIDTH_KEY, px)
                  : undefined
              }
            />
            {cols.map((c) => (
              <SortableHeader
                key={c.key}
                colKey={c.key}
                label={c.label}
                numeric={isNumeric(c.key)}
                sort={sort}
                onSortChange={onSortChange}
                width={widths?.[c.key]}
                onResize={
                  onWidthChange
                    ? (px) => onWidthChange(c.key, px)
                    : undefined
                }
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={1 + cols.length + (selection ? 1 : 0)}
                className={emptyContent ? undefined : "center muted"}
                style={{ padding: emptyContent ? 0 : 40 }}
              >
                {emptyContent ?? emptyMessage(grain)}
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const id = idOf(row, grain);
            const selected = selection?.ids.has(id) ?? false;
            return (
              <Fragment key={keyOf(row, grain)}>
              <StatsRow
                row={row}
                grain={grain}
                cols={cols}
                onClick={
                  onRowClick && grain !== "keyword" ? () => onRowClick(row) : undefined
                }
                selectable={!!selection}
                selected={selected}
                onToggle={
                  selection ? (shift) => handleRowToggle(id, shift) : undefined
                }
                canEditMoney={canEditMoney && !readOnlyRowIds?.has(id)}
                ruleNamesWidth={widths?.rule_names}
                onCommitDailyBudget={onCommitDailyBudget}
                onCommitTargetCpa={onCommitTargetCpa}
                onCommitDefaultBid={onCommitDefaultBid}
                onCommitCpaGoal={onCommitCpaGoal}
                onCommitBid={onCommitBid}
              />
              {deviceSplit &&
                deviceSubRows(row.device_split).map((sub) => (
                  <DeviceSubRow
                    key={sub.device}
                    label={sub.label}
                    data={sub.data as CampaignStatsRow | AdGroupStatsRow | KeywordStatsRow | null}
                    grain={grain}
                    cols={cols}
                    selectable={!!selection}
                  />
                ))}
              </Fragment>
            );
          })}
          {/* Spacer rows so the Total / Sub Total tfoot rows never sit
           *  visually higher than the 10th row position. Without this,
           *  a table with 2-3 results parks Totals right under the
           *  data, which reads as "the Totals row is also a data row"
           *  on tight result sets. Rendered transparent + non-hoverable
           *  so they don't draw attention. */}
          {rows.length > 0 &&
            rows.length < MIN_ROWS_BEFORE_TOTALS &&
            Array.from({ length: MIN_ROWS_BEFORE_TOTALS - rows.length }).map(
              (_, i) => (
                <tr key={`spacer-${i}`} className="row-spacer" aria-hidden="true">
                  {selection && <td className="sticky-col sticky-col-checkbox" />}
                  <td className="sticky-col sticky-col-identity" />
                  {cols.map((c) => (
                    <td key={c.key} />
                  ))}
                </tr>
              ),
            )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            {/* Optional filtered-only row — only when in-table filters
             *  have narrowed the visible set (status / search / etc).
             *  Sums `serverTotals` (i.e. just the visible scope).
             *  Pinned to the bottom but offset upward by one row-h so
             *  the always-on Total-by row stacks underneath it. */}
            {showFilteredRow && (
              <tr className={`row-total row-total-filtered${footStack}`} style={footOff(3)}>
                {selection && <td className="sticky-col sticky-col-checkbox" />}
                <td className="sticky-col sticky-col-identity">
                  <span className="total-lbl">{filteredTotalsLabel}</span>
                  <span
                    className="muted"
                    style={{ marginLeft: 8, fontSize: 11.5 }}
                  >
                    {filteredCount}
                  </span>
                </td>
                {cols.map((c) => (
                  <td key={c.key} className={isNumeric(c.key) ? "num" : ""}>
                    {renderTotal(c.key, totals)}
                  </td>
                ))}
              </tr>
            )}
            {devicePlacement === "filtered" &&
              deviceLines.map((d, i) => (
                <DeviceTotalRow
                  key={d.device}
                  label={d.label}
                  data={d.data}
                  cols={cols}
                  selectable={!!selection}
                  className={footStack}
                  style={footOff(2 - i)}
                />
              ))}
            {/* Always-on "Total by …" row. Falls back to summing the
             *  visible page when no unfilteredTotals supplied — that
             *  preserves the legacy footer for old callers. */}
            <tr
              className={`row-total${footStack}`}
              style={footOff(devicePlacement === "single" ? 2 : 0)}
            >
              {selection && <td className="sticky-col sticky-col-checkbox" />}
              <td className="sticky-col sticky-col-identity">
                <span className="total-lbl">{bottomLabel}</span>
                <span
                  className="muted"
                  style={{ marginLeft: 8, fontSize: 11.5 }}
                >
                  {bottomCount} {countUnit}{bottomCount === 1 ? "" : "s"}
                </span>
              </td>
              {cols.map((c) => (
                <td key={c.key} className={isNumeric(c.key) ? "num" : ""}>
                  {renderTotal(c.key, bottomTotals)}
                </td>
              ))}
            </tr>
            {devicePlacement === "single" &&
              deviceLines.map((d, i) => (
                <DeviceTotalRow
                  key={d.device}
                  label={d.label}
                  data={d.data}
                  cols={cols}
                  selectable={!!selection}
                  className={footStack}
                  style={footOff(1 - i)}
                />
              ))}
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function StatsRow({
  row,
  grain,
  cols,
  onClick,
  selectable,
  selected,
  onToggle,
  canEditMoney,
  ruleNamesWidth,
  onCommitDailyBudget,
  onCommitTargetCpa,
  onCommitDefaultBid,
  onCommitCpaGoal,
  onCommitBid,
}: {
  row: CampaignStatsRow | AdGroupStatsRow | KeywordStatsRow;
  grain: Grain;
  cols: readonly { key: ColumnKey; label: string }[];
  onClick?: () => void;
  selectable: boolean;
  selected: boolean;
  onToggle?: (shiftKey: boolean) => void;
  canEditMoney: boolean;
  /** Ширина колонки Rule Names, заданная пользователем. */
  ruleNamesWidth?: number;
  onCommitDailyBudget?: (campaign_id: number, daily_budget: number) => void;
  onCommitTargetCpa?: (campaign_id: number, target_cpa: number) => void;
  onCommitDefaultBid?: (ad_group_id: number, default_bid: number) => void;
  onCommitCpaGoal?: (ad_group_id: number, cpa_goal: number) => void;
  onCommitBid?: (keyword_id: number, bid: number) => void;
}) {
  return (
    <tr>
      {selectable && (
        <td
          className="sticky-col sticky-col-checkbox"
          style={{ width: 36, paddingLeft: 14 }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) =>
              onToggle?.((e.nativeEvent as MouseEvent).shiftKey)
            }
            aria-label="Select row"
          />
        </td>
      )}
      <td className="sticky-col sticky-col-identity">
        {renderIdentity(row, grain, onClick)}
      </td>
      <MetricCells
        row={row}
        grain={grain}
        cols={cols}
        canEditMoney={canEditMoney}
        ruleNamesWidth={ruleNamesWidth}
        onCommitDailyBudget={onCommitDailyBudget}
        onCommitTargetCpa={onCommitTargetCpa}
        onCommitDefaultBid={onCommitDefaultBid}
        onCommitCpaGoal={onCommitCpaGoal}
        onCommitBid={onCommitBid}
      />
    </tr>
  );
}

/** One device's line under its row: indented label, no checkbox, no row
 *  actions, metrics from the device's own numbers (same cells, same
 *  colour rules), parameters left empty. No data on the device → dashes. */
function DeviceSubRow({
  label,
  data,
  grain,
  cols,
  selectable,
}: {
  label: string;
  data: CampaignStatsRow | AdGroupStatsRow | KeywordStatsRow | null;
  grain: Grain;
  cols: readonly { key: ColumnKey; label: string }[];
  selectable: boolean;
}) {
  return (
    <tr className="dev-sub">
      {selectable && <td className="sticky-col sticky-col-checkbox" />}
      <td className="sticky-col sticky-col-identity">
        <span className="dev-sub-label">{label}</span>
      </td>
      {data ? (
        <MetricCells row={data} grain={grain} cols={cols} canEditMoney={false} subRow />
      ) : (
        cols.map((c) =>
          splitsByDevice(c.key) ? (
            <td key={c.key} className="num">—</td>
          ) : (
            <td key={c.key} />
          ),
        )
      )}
    </tr>
  );
}

/** «Total for iPhone» / «Total for iPad» — a device line of the footer,
 *  styled like the body's device lines (indent, muted, not bold, no
 *  count). Values go through the same `renderTotal` as the Total filtered
 *  row above it; columns that don't split by device, and a device with no
 *  data anywhere in the filtered set, show "—". */
function DeviceTotalRow({
  label,
  data,
  cols,
  selectable,
  className,
  style,
}: {
  label: string;
  data: EntityTotalsRow | null;
  cols: readonly { key: ColumnKey; label: string }[];
  selectable: boolean;
  className: string;
  style: CSSProperties | undefined;
}) {
  const t = data ? totalsFromServer(data) : null;
  return (
    <tr className={`dev-sub dev-sub-total${className}`} style={style}>
      {selectable && <td className="sticky-col sticky-col-checkbox" />}
      <td className="sticky-col sticky-col-identity">
        <span className="dev-sub-label">{label}</span>
      </td>
      {cols.map((c) => (
        <td key={c.key} className={isNumeric(c.key) ? "num" : ""}>
          {t && splitsByDevice(c.key) ? renderTotal(c.key, t) : "—"}
        </td>
      ))}
    </tr>
  );
}

function renderIdentity(
  row: CampaignStatsRow | AdGroupStatsRow | KeywordStatsRow,
  grain: Grain,
  onClick: (() => void) | undefined
): ReactNode {
  if (grain === "campaign") {
    const r = row as CampaignStatsRow;
    const initials = (r.campaign_name ?? "??").slice(0, 2).toUpperCase();
    return (
      <span className="tree-cell">
        {r.app_icon_url ? (
          <img
            src={r.app_icon_url}
            alt=""
            className="row-ico-img"
            // Defensive: if the artwork URL 404s (rare — Apple
            // sometimes rotates assets), render the fallback badge
            // by hiding the broken img and letting the next sibling
            // span :empty trick won't help; instead we just blank
            // out the alt-text so there's no broken-image icon.
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <span className="row-ico">{initials}</span>
        )}
        <LinkLikeLabel
          text={r.campaign_name ?? `Campaign ${r.campaign_id}`}
          onClick={onClick}
        />
      </span>
    );
  }
  if (grain === "ad_group") {
    const r = row as AdGroupStatsRow;
    return (
      <span className="tree-cell">
        <span className="row-ico gray">AG</span>
        <LinkLikeLabel
          text={r.ad_group_name ?? `Ad group ${r.ad_group_id}`}
          onClick={onClick}
        />
      </span>
    );
  }
  const r = row as KeywordStatsRow;
  // Match-type convention matches what Apple Search Ads renders
  // inline in its own UI: EXACT keywords wear square brackets, BROAD
  // keywords are plain. No trailing "· EXACT / · BROAD" tag — the
  // brackets are the signal.
  const rawText = r.keyword_text ?? `Keyword ${r.keyword_id}`;
  const label =
    r.match_type === "EXACT" ? `[${rawText}]` : rawText;
  return (
    <span className="tree-cell">
      <span className="row-ico kw">kw</span>
      <span>{label}</span>
    </span>
  );
}

function LinkLikeLabel({
  text,
  onClick,
}: {
  text: string;
  onClick?: () => void;
}) {
  if (!onClick) {
    return <span style={{ fontWeight: 500 }}>{text}</span>;
  }
  return (
    <a
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        color: "var(--c-text)",
        fontWeight: 500,
        cursor: "pointer",
        borderBottom: "1px dashed transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "var(--c-accent-600)";
        e.currentTarget.style.borderBottomColor = "var(--c-accent-400)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "var(--c-text)";
        e.currentTarget.style.borderBottomColor = "transparent";
      }}
    >
      {text}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Rule names cell — comma-separated list, full set in a hover tooltip
// that follows the cursor and disappears on mouse leave. Tooltip is a
// fixed-position div so it can escape the table-scroll's
// overflow:hidden clipping (which would happen with absolute pos).
// ---------------------------------------------------------------------------

/** Ширина колонки Rule Names по умолчанию.
 *
 *  ★ Без явного потолка колонка растёт под самый длинный список: у
 *  таблицы авторазметка, а `.dt tbody td` держит `white-space: nowrap`,
 *  поэтому заданная пользователем ширина заголовка ни на что не влияет —
 *  содержимое распирает столбец, и чем больше правил применено, тем
 *  сильнее уезжает вся таблица. Потолок ставится на ЯЧЕЙКУ, иначе
 *  перетаскивание ручки не держится.
 */
const RULE_NAMES_DEFAULT_W = 200;

function RuleNamesCell({ names, width }: { names: string[]; width?: number }) {
  // The hovered cell itself is the anchor — the popover opens beside it
  // (right/left, below/above by viewport room, AnchoredHoverPopover), not at
  // the cursor, so it never runs off the window when the column is last.
  const [anchor, setAnchor] = useState<HTMLTableCellElement | null>(null);
  const [tooNarrow, setTooNarrow] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);

  const w = width ?? RULE_NAMES_DEFAULT_W;
  const full = names.join(" · ");
  const count = `${names.length} ${names.length === 1 ? "rule" : "rules"}`;

  // Помещается ли список — вопрос к разметке, а не к длине строки: ширину
  // колонки задаёт пользователь и меняет перетаскиванием.
  //
  // ★ Мерка живёт в ОТДЕЛЬНОМ скрытом пролёте, который всегда держит
  // полный текст. Мерить видимое содержимое нельзя: подменив его на счёт,
  // мы бы получили «теперь помещается» и вернули список обратно — и так
  // до бесконечности.
  useLayoutEffect(() => {
    const box = boxRef.current;
    const probe = probeRef.current;
    if (!box || !probe) return;
    const check = () => setTooNarrow(probe.offsetWidth > box.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(box);
    return () => ro.disconnect();
  }, [full, w]);

  return (
    <td
      onMouseEnter={(e) => setAnchor(e.currentTarget)}
      onMouseLeave={() => setAnchor(null)}
      style={{ width: w, maxWidth: w, overflow: "hidden", cursor: "default" }}
    >
      <div
        ref={boxRef}
        style={{
          position: "relative",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {tooNarrow ? count : full}
        <span
          ref={probeRef}
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            visibility: "hidden",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {full}
        </span>
      </div>
      <AnchoredHoverPopover anchor={anchor}>
        <ul className="rule-names-popover">
          {names.map((n, idx) => (
            <li key={`${idx}:${n}`}>{n}</li>
          ))}
        </ul>
      </AnchoredHoverPopover>
    </td>
  );
}


// ---------------------------------------------------------------------------
// Metric cells (shared across grains)
// ---------------------------------------------------------------------------

function MetricCells({
  row,
  grain,
  cols,
  canEditMoney,
  ruleNamesWidth,
  onCommitDailyBudget,
  onCommitTargetCpa,
  onCommitDefaultBid,
  onCommitCpaGoal,
  onCommitBid,
  subRow = false,
}: {
  row: CampaignStatsRow | AdGroupStatsRow | KeywordStatsRow;
  grain: Grain;
  cols: readonly { key: ColumnKey; label: string }[];
  canEditMoney: boolean;
  /** A device sub-row: parameter columns render empty. */
  subRow?: boolean;
  ruleNamesWidth?: number;
  onCommitDailyBudget?: (campaign_id: number, daily_budget: number) => void;
  onCommitTargetCpa?: (campaign_id: number, target_cpa: number) => void;
  onCommitDefaultBid?: (ad_group_id: number, default_bid: number) => void;
  onCommitCpaGoal?: (ad_group_id: number, cpa_goal: number) => void;
  onCommitBid?: (keyword_id: number, bid: number) => void;
}) {
  const labelsCtx = useLabelsContext();
  const toNum = (v: string | null | undefined): number | null => {
    if (v === null || v === undefined) return null;
    const parsed = parseFloat(v);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const ttr = row.impressions > 0 ? (row.taps / row.impressions) * 100 : null;
  const avgCpt =
    row.taps > 0 && toNum(row.spend_usd) !== null
      ? (toNum(row.spend_usd) as number) / row.taps
      : null;

  return (
    <>
      {cols.map((c) => {
        if (subRow && !splitsByDevice(c.key)) return <td key={c.key} />;
        // Adjust per-event columns (source='adjust' apps) — read from
        // the row.adjust block. ColumnKey `adj_<rest>` → adjust["<rest>"].
        // CR is a 0..1 ratio (×100 on render); cpa/revenue are money;
        // bare event key is a count.
        if (c.key.startsWith("adj_")) {
          const adjKey = c.key.slice(4);
          const raw = row.adjust ? row.adjust[adjKey] : null;
          const v = raw === undefined ? null : raw;
          if (c.key.endsWith("_cr")) {
            return (
              <td key={c.key} className="num">
                {v === null ? "—" : (v * 100).toFixed(2) + "%"}
              </td>
            );
          }
          if (c.key.endsWith("_cpa") || c.key.endsWith("_revenue")) {
            return (
              <td key={c.key} className="num money">
                {v === null ? "—" : "$" + fmtMoney(v)}
              </td>
            );
          }
          return (
            <td key={c.key} className="num">
              {v === null ? "—" : fmtInt(v)}
            </td>
          );
        }
        switch (c.key) {
          case "status": {
            // Campaign rows carry both `status` (user toggle) and
            // `display_status` (Apple's deliverability state). Ad
            // group / keyword rows currently only carry `status`,
            // so the cast tolerates a missing display_status field
            // without complaint. StatusDot prefers display when
            // present — that's where ON_HOLD/OUT_OF_BUDGET surface.
            const r = row as {
              status?: string | null;
              display_status?: string | null;
            };
            return (
              <td key={c.key} onClick={(e) => e.stopPropagation()}>
                <StatusDot
                  status={r.status ?? null}
                  displayStatus={r.display_status ?? null}
                />
              </td>
            );
          }
          case "label": {
            // Label is on campaign + ad_group only (not keywords).
            // Look up via the LabelsContext provided by the page.
            const r = row as {
              campaign_id?: number;
              ad_group_id?: number;
              keyword_id?: number;
            };
            const isAdGroup = "ad_group_id" in r && !("keyword_id" in r);
            const entityId = isAdGroup ? r.ad_group_id : r.campaign_id;
            const entityType: "campaign" | "ad_group" = isAdGroup
              ? "ad_group"
              : "campaign";
            const labels =
              labelsCtx && entityId != null
                ? labelsCtx.labelsByEntity[entityType].get(entityId) ?? []
                : [];
            if (labels.length === 0) {
              return (
                <td key={c.key} className="val-muted">
                  —
                </td>
              );
            }
            // Labels flow horizontally inside the cell — NO wrap to a
            // second line (row height stays fixed). When the cell is
            // narrower than the chip strip, overflow is clipped on the
            // right. Full list surfaces via the td `title` tooltip on
            // hover. User widens the column via the resize handle on
            // the column edge to see more inline.
            const titleText = labels.map((l) => l.name).join(", ");
            return (
              <td
                key={c.key}
                className="cell-labels"
                title={titleText}
              >
                <span
                  style={{
                    display: "inline-flex",
                    gap: 4,
                    flexWrap: "nowrap",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                >
                  {labels.map((lab) => (
                    <LabelChip key={lab.id} name={lab.name} />
                  ))}
                </span>
              </td>
            );
          }
          case "ad_group_id":
            if ("ad_group_id" in row) {
              return (
                <td key={c.key} className="num mono">
                  {row.ad_group_id}
                </td>
              );
            }
            return (
              <td key={c.key} className="num val-muted">
                —
              </td>
            );
          case "daily_budget": {
            const b = (row as { daily_budget?: string | null }).daily_budget;
            if (grain === "campaign" && "campaign_id" in row) {
              const campaign_id = row.campaign_id;
              return (
                <BidCell
                  key={c.key}
                  value={b ?? null}
                  editable={canEditMoney}
                  onCommit={(v) => onCommitDailyBudget?.(campaign_id, v)}
                />
              );
            }
            return (
              <td key={c.key} className="num money">
                {toNum(b) === null ? "—" : "$" + fmtMoneyTrim(toNum(b))}
              </td>
            );
          }
          case "default_bid": {
            const b = (row as { default_bid?: string | null }).default_bid;
            if (grain === "ad_group" && "ad_group_id" in row) {
              const ad_group_id = row.ad_group_id;
              return (
                <BidCell
                  key={c.key}
                  value={b ?? null}
                  editable={canEditMoney}
                  onCommit={(v) => onCommitDefaultBid?.(ad_group_id, v)}
                />
              );
            }
            return (
              <td key={c.key} className="num money">
                {toNum(b) === null ? "—" : "$" + fmtMoneyTrim(toNum(b))}
              </td>
            );
          }
          case "cpa_goal": {
            const g = (row as { cpa_goal?: string | null }).cpa_goal;
            // Inline-editable on ad_group grain only — ASA's cpa_goal
            // is an ad_group-level setting (not campaign or keyword).
            // Same click-to-edit UX as the other money cells.
            if (grain === "ad_group" && "ad_group_id" in row) {
              const ad_group_id = row.ad_group_id;
              return (
                <BidCell
                  key={c.key}
                  value={g ?? null}
                  editable={canEditMoney}
                  onCommit={(v) => onCommitCpaGoal?.(ad_group_id, v)}
                />
              );
            }
            return (
              <td key={c.key} className="num money">
                {toNum(g) === null ? "—" : "$" + fmtMoneyTrim(toNum(g))}
              </td>
            );
          }
          case "bid":
            if ("keyword_id" in row) {
              const keyword_id = row.keyword_id;
              const bidVal = (row as { bid?: string | null }).bid ?? null;
              return (
                <BidCell
                  key={c.key}
                  value={bidVal}
                  editable={canEditMoney && grain === "keyword"}
                  onCommit={(bid) => onCommitBid?.(keyword_id, bid)}
                />
              );
            }
            return (
              <td key={c.key} className="num val-muted">
                —
              </td>
            );
          case "impressions":
            return <td key={c.key} className="num">{fmtInt(row.impressions)}</td>;
          case "ttr":
            return (
              <td key={c.key} className="num">
                {ttr === null ? "—" : ttr.toFixed(2) + "%"}
              </td>
            );
          case "taps":
            return <td key={c.key} className="num">{fmtInt(row.taps)}</td>;
          case "avg_cpt":
            return (
              <td key={c.key} className="num">
                {avgCpt === null ? "—" : "$" + fmtMoney(avgCpt)}
              </td>
            );
          case "spend":
            return (
              <td key={c.key} className="num money">
                ${fmtMoney(toNum(row.spend_usd))}
              </td>
            );
          case "revenue":
            // Single "total revenue" aggregate — sums all classified
            // event revenue server-side (refunds subtract via signed
            // net_revenue_usd). User's request 2026-04-30: keep the
            // 72 per-event_revenue cells but offer one cell that
            // shows the full picture.
            return (
              <td key={c.key} className="num money">
                {toNum(row.revenue_usd) === null
                  ? "—"
                  : "$" + fmtMoney(toNum(row.revenue_usd))}
              </td>
            );
          case "installs":
            return <td key={c.key} className="num">{fmtInt(row.installs)}</td>;
          case "cpa":
            return <CpaCell key={c.key} v={toNum(row.cpa)} />;
          case "roas":
            return <RoasCell key={c.key} v={toNum(row.roas)} />;
          case "roas_d1":
            return <RoasCell key={c.key} v={toNum((row as { roas_d1?: string | null }).roas_d1)} />;
          case "roas_d4":
            return <RoasCell key={c.key} v={toNum(row.roas_d4)} />;
          case "roas_d7":
            return <RoasCell key={c.key} v={toNum(row.roas_d7)} />;
          case "roas_d10":
            return <RoasCell key={c.key} v={toNum((row as { roas_d10?: string | null }).roas_d10)} />;
          case "roas_d20":
            return <RoasCell key={c.key} v={toNum((row as { roas_d20?: string | null }).roas_d20)} />;
          case "roas_d30":
            return <RoasCell key={c.key} v={toNum(row.roas_d30)} />;
          case "roas_d35":
            return <RoasCell key={c.key} v={toNum((row as { roas_d35?: string | null }).roas_d35)} />;

          case "roi_d1":
          case "roi_d4":
          case "roi_d7":
          case "roi_d10":
          case "roi_d20":
          case "roi_d30":
          case "roi_d35": {
            const v = toNum(
              (row as unknown as Record<string, string | null>)[c.key]
            );
            return (
              <td key={c.key} className="num">
                {v === null ? "—" : (v * 100).toFixed(1) + "%"}
              </td>
            );
          }

          case "arpu_d1":
          case "arpu_d4":
          case "arpu_d7":
          case "arpu_d10":
          case "arpu_d20":
          case "arpu_d30":
          case "arpu_d35": {
            const v = toNum(
              (row as unknown as Record<string, string | null>)[c.key]
            );
            return (
              <td key={c.key} className="num money">
                {v === null ? "—" : "$" + fmtMoney(v)}
              </td>
            );
          }

          // Parameter columns populated from asa_*_meta JOINs.
          case "campaign_id":
            if ("campaign_id" in row) {
              return (
                <td key={c.key} className="num mono">{row.campaign_id}</td>
              );
            }
            return <td key={c.key} className="num val-muted">—</td>;
          case "placement": {
            const p = (row as { placement?: string | null }).placement;
            return <td key={c.key}>{p ?? "—"}</td>;
          }
          case "country": {
            const p = (row as { country?: string | null }).country;
            return <td key={c.key}>{p ?? "—"}</td>;
          }
          case "target_cpa": {
            const p = (row as { target_cpa?: string | null }).target_cpa;
            // Inline-editable on Campaigns tab — pushes to ASA via
            // PATCH /api/campaigns/:id { target_cpa }, drainer wraps in
            // {"campaign": ...} envelope. NULL on campaigns whose bid
            // lives elsewhere (manual-CPC, where the bid is on ad_group).
            if (grain === "campaign" && "campaign_id" in row) {
              const campaign_id = row.campaign_id;
              return (
                <BidCell
                  key={c.key}
                  value={p ?? null}
                  editable={canEditMoney}
                  onCommit={(v) => onCommitTargetCpa?.(campaign_id, v)}
                />
              );
            }
            return (
              <td key={c.key} className="num money">
                {toNum(p) === null ? "—" : "$" + fmtMoney(toNum(p))}
              </td>
            );
          }
          case "active_cpp": {
            const has = (row as { has_cpp?: boolean }).has_cpp;
            return (
              <td key={c.key}>
                {has === true ? "Yes" : has === false ? "No" : "—"}
              </td>
            );
          }
          case "last_modified_at": {
            // Platform-driven edit stamp (status / budget / target_cpa /
            // cpa_goal / default_bid / bid / creation via Duplicate).
            // NULL = entity never edited through the platform → "—".
            const lm = (row as { last_modified_at?: string | null })
              .last_modified_at;
            return (
              <td key={c.key} title={lm ?? ""}>
                {lm ? shortDateTime(lm) : "—"}
              </td>
            );
          }
          case "creation_time": {
            // ASA's own entity creationTime (immutable). NULL until the
            // collector has re-listed this entity at least once → "—".
            const ct = (row as { creation_time?: string | null })
              .creation_time;
            return (
              <td key={c.key} title={ct ?? ""}>
                {ct ? shortDate(ct) : "—"}
              </td>
            );
          }
          case "modified_by": {
            // 'manual' | 'by_rule' | 'by_autopilot'. NULL when
            // last_modified_at is NULL (never edited via platform).
            const mb = (row as { modified_by?: string | null }).modified_by;
            if (!mb) return <td key={c.key}>—</td>;
            const label =
              mb === "manual" ? "Manual"
              : mb === "by_rule" ? "By rule"
              : mb === "by_autopilot" ? "By autopilot"
              : mb;
            return <td key={c.key}>{label}</td>;
          }
          case "rule_applied": {
            // Green dot if any rule applies to this entity (per-grain
            // semantics — see stats_service correlated sub-select).
            // Gray otherwise.
            const names =
              (row as { applied_rule_names?: string[] }).applied_rule_names ?? [];
            const on = names.length > 0;
            return (
              <td key={c.key} title={on ? names.join(", ") : "No rules applied"}>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: on
                      ? "var(--c-pos-500, #22c55e)"
                      : "var(--c-text-4, #cbd2dc)",
                  }}
                  aria-label={on ? "Rule applied" : "No rules"}
                />
              </td>
            );
          }
          case "rule_names": {
            // «ECG · 5» — правило и число сущностей под строкой, которые оно
            // накрывает (ТЗ ECG §15); у правила уровня самой строки числа нет.
            const rules = (row as { applied_rules?: AppliedRule[] }).applied_rules;
            const names = rules
              ? rules.map((r) => ruleChipLabel(r))
              : (row as { applied_rule_names?: string[] }).applied_rule_names ?? [];
            if (names.length === 0) {
              return (
                <td key={c.key} className="val-muted">
                  —
                </td>
              );
            }
            return (
              <RuleNamesCell
                key={c.key}
                names={names}
                width={ruleNamesWidth}
              />
            );
          }

          // ASA install / download breakdown (added 2026-04-29).
          // Backend column names use `tap_installs` etc; UI keys use
          // `installs_tap` etc — translation table inline below.
          case "installs_tap": {
            const v = (row as unknown as Record<string, unknown>).tap_installs;
            return <td key={c.key} className="num">{typeof v === "number" ? fmtInt(v) : "—"}</td>;
          }
          case "installs_view": {
            const v = (row as unknown as Record<string, unknown>).view_installs;
            return <td key={c.key} className="num">{typeof v === "number" ? fmtInt(v) : "—"}</td>;
          }
          case "installs_total": {
            const v = (row as unknown as Record<string, unknown>).total_installs;
            return <td key={c.key} className="num">{typeof v === "number" ? fmtInt(v) : "—"}</td>;
          }
          case "cpa_tap":
          case "cpa_view":
          case "cpa_total": {
            const v = toNum(
              (row as unknown as Record<string, string | null>)[c.key]
            );
            return (
              <td key={c.key} className="num money">
                {v === null ? "—" : "$" + fmtMoney(v)}
              </td>
            );
          }
          case "new_downloads_tap":
          case "new_downloads_view":
          case "new_downloads_total":
          case "redownloads_tap":
          case "redownloads_view":
          case "redownloads_total": {
            const v = (row as unknown as Record<string, unknown>)[c.key];
            return <td key={c.key} className="num">{typeof v === "number" ? fmtInt(v) : "—"}</td>;
          }

          // Per-event-type conversion breakdown — full Adapty v2 webhook
          // coverage (18 native events × 4 metrics). Each event group
          // is rendered identically via the four case-clusters below;
          // refund events (subscription_refunded,
          // non_subscription_purchase_refunded) carry signed-negative
          // revenue and `fmtMoney` already handles the minus sign.
          case "trial_started":
          case "trial_converted":
          case "trial_renewal_cancelled":
          case "trial_renewal_reactivated":
          case "trial_expired":
          case "subscription_started":
          case "subscription_renewed":
          case "subscription_renewal_cancelled":
          case "subscription_renewal_reactivated":
          case "subscription_expired":
          case "subscription_paused":
          case "subscription_deferred":
          case "subscription_refunded":
          case "non_subscription_purchase":
          case "non_subscription_purchase_refunded":
          case "entered_grace_period":
          case "billing_issue_detected":
          case "access_level_updated": {
            const v = (row as unknown as Record<string, unknown>)[c.key];
            return (
              <td key={c.key} className="num">
                {typeof v === "number" ? fmtInt(v) : "—"}
              </td>
            );
          }
          case "trial_started_revenue":
          case "trial_converted_revenue":
          case "trial_renewal_cancelled_revenue":
          case "trial_renewal_reactivated_revenue":
          case "trial_expired_revenue":
          case "subscription_started_revenue":
          case "subscription_renewed_revenue":
          case "subscription_renewal_cancelled_revenue":
          case "subscription_renewal_reactivated_revenue":
          case "subscription_expired_revenue":
          case "subscription_paused_revenue":
          case "subscription_deferred_revenue":
          case "subscription_refunded_revenue":
          case "non_subscription_purchase_revenue":
          case "non_subscription_purchase_refunded_revenue":
          case "entered_grace_period_revenue":
          case "billing_issue_detected_revenue":
          case "access_level_updated_revenue": {
            const v = toNum(
              (row as unknown as Record<string, string | null>)[c.key]
            );
            return (
              <td key={c.key} className="num money">
                {v === null ? "—" : "$" + fmtMoney(v)}
              </td>
            );
          }
          case "trial_started_cr":
          case "trial_converted_cr":
          case "trial_renewal_cancelled_cr":
          case "trial_renewal_reactivated_cr":
          case "trial_expired_cr":
          case "subscription_started_cr":
          case "subscription_renewed_cr":
          case "subscription_renewal_cancelled_cr":
          case "subscription_renewal_reactivated_cr":
          case "subscription_expired_cr":
          case "subscription_paused_cr":
          case "subscription_deferred_cr":
          case "subscription_refunded_cr":
          case "non_subscription_purchase_cr":
          case "non_subscription_purchase_refunded_cr":
          case "entered_grace_period_cr":
          case "billing_issue_detected_cr":
          case "access_level_updated_cr": {
            const v = toNum(
              (row as unknown as Record<string, string | null>)[c.key]
            );
            return (
              <td key={c.key} className="num">
                {v === null ? "—" : (v * 100).toFixed(2) + "%"}
              </td>
            );
          }
          case "trial_started_cpa":
          case "trial_converted_cpa":
          case "trial_renewal_cancelled_cpa":
          case "trial_renewal_reactivated_cpa":
          case "trial_expired_cpa":
          case "subscription_started_cpa":
          case "subscription_renewed_cpa":
          case "subscription_renewal_cancelled_cpa":
          case "subscription_renewal_reactivated_cpa":
          case "subscription_expired_cpa":
          case "subscription_paused_cpa":
          case "subscription_deferred_cpa":
          case "subscription_refunded_cpa":
          case "non_subscription_purchase_cpa":
          case "non_subscription_purchase_refunded_cpa":
          case "entered_grace_period_cpa":
          case "billing_issue_detected_cpa":
          case "access_level_updated_cpa": {
            const v = toNum(
              (row as unknown as Record<string, string | null>)[c.key]
            );
            return (
              <td key={c.key} className="num money">
                {v === null ? "—" : "$" + fmtMoney(v)}
              </td>
            );
          }

          // Real-data columns added 2026-04-30 to round out the
          // ad-group / keyword pickers per ASA's per-level fields.
          case "keyword_id": {
            const kw = (row as { keyword_id?: number | null }).keyword_id;
            return (
              <td key={c.key} className="num mono">
                {typeof kw === "number" ? kw : "—"}
              </td>
            );
          }
          case "translation_en": {
            // Empty, not "—", for everything except a real translation: an
            // absent translation is not missing data the user should chase.
            // TranslationValue owns that rule for every grid.
            const tr = row as {
              translation_en?: string | null;
              translation_status?: string | null;
            };
            return (
              <td key={c.key} className="tl-cell">
                <TranslationValue
                  status={tr.translation_status}
                  text={tr.translation_en}
                />
              </td>
            );
          }
          case "match_type": {
            const mt = (row as { match_type?: string | null }).match_type;
            return (
              <td key={c.key} className={mt ? "" : "val-muted"}>
                {mt ?? "—"}
              </td>
            );
          }
          case "cr_tap": {
            // Tap-CR = tap_installs / taps. Both fields come from ASA's
            // daily report; render as percent. Same gating as `ttr`:
            // "—" when taps == 0 to avoid divide-by-zero.
            const tInst = (row as { tap_installs?: number | null }).tap_installs ?? 0;
            const taps = row.taps ?? 0;
            return (
              <td key={c.key} className="num">
                {taps > 0 ? ((tInst / taps) * 100).toFixed(2) + "%" : "—"}
              </td>
            );
          }
          case "cr_total": {
            const totalInst = (row as { total_installs?: number | null }).total_installs ?? 0;
            const taps = row.taps ?? 0;
            return (
              <td key={c.key} className="num">
                {taps > 0 ? ((totalInst / taps) * 100).toFixed(2) + "%" : "—"}
              </td>
            );
          }

          case "ipm": {
            // IPM = installs per 1000 impressions. Apple-style metric;
            // null when impressions == 0 to avoid divide-by-zero.
            const imp = row.impressions ?? 0;
            const inst = row.installs ?? 0;
            const v = imp > 0 ? (inst / imp) * 1000 : null;
            return (
              <td key={c.key} className="num">
                <IpmBadge v={v} />
              </td>
            );
          }
          case "cpm": {
            // CPM = cost per 1000 impressions. Standard formula.
            const imp = row.impressions ?? 0;
            const spendNum = toNum(row.spend_usd);
            return (
              <td key={c.key} className="num money">
                {imp > 0 && spendNum !== null
                  ? "$" + fmtMoney((spendNum / imp) * 1000)
                  : "—"}
              </td>
            );
          }
          case "active_keywords": {
            const v = (row as { active_keywords?: number | null })
              .active_keywords;
            return (
              <td key={c.key} className="num">
                {typeof v === "number" ? fmtInt(v) : "—"}
              </td>
            );
          }
          case "search_match": {
            // ASA's automatedKeywordsOptIn — boolean toggle on the
            // ad_group. Renders Yes/No; "—" when the field is absent
            // (campaign / keyword grain).
            const v = (row as { search_match?: boolean | null }).search_match;
            return (
              <td key={c.key}>
                {v === true ? "Yes" : v === false ? "No" : "—"}
              </td>
            );
          }
          case "devices":
          case "gender":
          case "age":
          case "locality":
          case "admin_area": {
            // ASA targeting facets — list of strings or null
            // ("unrestricted"). The column key uses the singular
            // human-readable form; the row carries the plural backend
            // alias.
            const aliasMap: Record<string, keyof AdGroupStatsRow | undefined> = {
              devices: "devices",
              gender: "genders",
              age: "ages",
              locality: "localities",
              admin_area: "admin_areas",
            };
            const alias = aliasMap[c.key as string];
            const arr = alias
              ? ((row as unknown as Record<string, unknown>)[alias] as
                  | string[]
                  | null
                  | undefined)
              : undefined;
            if (!arr) {
              return (
                <td key={c.key} className="val-muted">
                  All
                </td>
              );
            }
            if (arr.length === 0) {
              return (
                <td key={c.key} className="val-muted">
                  —
                </td>
              );
            }
            return (
              <td key={c.key} title={arr.join(", ")}>
                {arr.length <= 3 ? arr.join(", ") : `${arr.slice(0, 2).join(", ")} +${arr.length - 2}`}
              </td>
            );
          }

          // Placeholder cells for columns whose backend data isn't
          // wired yet — kept contiguous so TS exhaustive-switch + the
          // no-fallthrough lint stay happy.
          case "rules":
          case "tracked_installs":
          case "qs_status":
          case "goal_based_opt":
          case "tags":
          case "active_capacity":
          case "active_share":
          case "organic_search":
          case "organic_position":
          case "sov_2d":
          case "sov_5d":
            return <td key={c.key} className="num val-muted">—</td>;

          // Apple share of voice by ad slot — keyword / ad-group grain (filled
          // by stats_service._attach_keyword_sov / _attach_ad_group_sov). "—" on
          // campaign rows and on keywords with no SOV (multi-country campaign, or
          // no data for the query). The share is Apple's — the app's share of
          // the query, NOT this keyword's traffic; don't sum it.
          case "impression_share":
          case "sov_slot2":
          case "sov_total": {
            const [lo, hi, what] =
              c.key === "impression_share"
                ? [row.sov_low_share, row.sov_high_share, "first place"]
                : c.key === "sov_total"
                  ? [row.sov_all_low_share, row.sov_all_high_share, "any place"]
                  : [row.sov_slot2_low_share, row.sov_slot2_high_share, "second place"];
            const has = lo != null && hi != null;
            const days = row.sov_days ?? 0;
            return (
              <td
                key={c.key}
                className={has ? "num" : "num val-muted"}
                title={
                  has
                    ? `Apple's share of voice for this keyword's query on the ${what}, across ${days} ` +
                      "day(s) with data. The app's share of the query — not this " +
                      "keyword's traffic; don't sum it. Shown only for single-country campaigns."
                    : "No share of voice yet (multi-country campaign, or no SOV data for this query)"
                }
              >
                {fmtSovShareCell(lo, hi)}
              </td>
            );
          }
          case "search_rank": {
            const has = row.sov_rank_int != null;
            return (
              <td key={c.key} className={has ? "num" : "num val-muted"}>
                {fmtSovRankCell(row.sov_rank_int, row.sov_rank_raw)}
              </td>
            );
          }
          case "search_ads_popularity":
            return (
              <td
                key={c.key}
                className={
                  row.sov_search_popularity != null ? "num" : "num val-muted"
                }
              >
                {row.sov_search_popularity ?? "—"}
              </td>
            );
        }
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isNumeric(key: ColumnKey): boolean {
  // Drives right-alignment of the header. Everything in this table is a metric
  // except these two: a status chip and a phrase of prose.
  return key !== "status" && key !== "translation_en";
}

function emptyMessage(grain: Grain): string {
  if (grain === "campaign") return "No campaigns match the current filters.";
  if (grain === "ad_group") return "This campaign has no ad groups.";
  return "This ad group has no keywords.";
}

/** One footer cell. The number comes from `totalValue` — the single place
 *  the footer's arithmetic lives, shared by Total filtered, Total by … and
 *  the device totals (see lib/footer-totals.ts). */
function renderTotal(key: ColumnKey, t: Totals): ReactNode {
  const val = totalValue(key, t);
  if (val?.kind === "ipm") return <IpmBadge v={val.v} />;
  return formatTotal(val);
}

function shortDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

// Date-only (with YEAR) for "Created Date" — a creation can be months
// or years back, so unlike shortDateTime we keep the year and drop the
// time. UTC to match ASA's timestamps.
function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

// Apple's Share-of-Voice band, low..high — never a midpoint (Apple withholds
// the exact figure; collapsing it would invent precision). Same rendering as
// the Search Terms tab's fmtSovShare.
function fmtSovShareCell(
  lo: number | null | undefined,
  hi: number | null | undefined,
): string {
  if (lo == null || hi == null) return "—";
  const l = Math.round(lo * 100);
  const h = Math.round(hi * 100);
  return l === h ? `${l}%` : `${l}–${h}%`;
}

// v1 ranks are plain integers (best ALL-slot place over the days); the v5
// censored tail (">5") is gone with the v5 feed.
function fmtSovRankCell(
  rankInt: number | null | undefined,
  rankRaw: string | null | undefined,
): string {
  if (rankInt != null) return String(rankInt);
  if (rankRaw === "GREATER_THAN_FIVE") return ">5";
  return "—";
}

function idOf(
  row: CampaignStatsRow | AdGroupStatsRow | KeywordStatsRow,
  grain: Grain
): number {
  if (grain === "campaign") return (row as CampaignStatsRow).campaign_id;
  if (grain === "ad_group") return (row as AdGroupStatsRow).ad_group_id;
  return (row as KeywordStatsRow).keyword_id;
}

function keyOf(
  row: CampaignStatsRow | AdGroupStatsRow | KeywordStatsRow,
  grain: Grain
): string {
  return `${grain[0]}-${idOf(row, grain)}`;
}
